import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectGroup } from '../../shared/interfaces/project-group.interface';
import { ProjectRouteStationWithParam } from '../../shared/interfaces/project-route-station.class';
import { ProjectRouteStation } from '../../shared/interfaces/project-route-station.interface';
import { Project } from '../../shared/interfaces/project.interface';
import { ProjectDataService } from '../../shared/services/project-data.service';
import { MapHandlerService } from '../../project-map/shared/map-handler.service';
import { MapPosition } from '../../project-map/shared/map-position.interface';
import { MapPositionService } from '../../project-map/shared/map-position.service';
import { Router } from '@angular/router';
import { MapConsentService } from 'src/app/shared/map-consent/shared/map-consent.service'

@Component({
  selector: 'avs-project-settings-map',
  templateUrl: './project-settings-map.component.html',
  styleUrls: ['./project-settings-map.component.css']
})
export class ProjectSettingsMapComponent implements OnInit, OnDestroy {
  public isMapConsentGiven: boolean = false;
  private map?: google.maps.Map;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private subscriptions: Subscription[] = [];

  private projects: Project[] = [];
  private projectGroups: ProjectGroup[] = [];
  private projectRouteStations: ProjectRouteStation[] = [];
  private projectRouteStationsWithParam: ProjectRouteStationWithParam[] = [];
  private activeProject?: Project;
  private activeGroup?: ProjectGroup;
  private activeRouteStation?: ProjectRouteStation;

  constructor(private mapPositionService: MapPositionService,
    private readonly cdr: ChangeDetectorRef,
    private mapHandler: MapHandlerService,
    private projectDataService: ProjectDataService,
    public router: Router,
    private mapConsentService:MapConsentService) { }

  public ngOnInit(): void {
    this.initMapHandler();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.clearMarkers();
  }

  private async initMapHandler(): Promise<void> {
    this.subscriptions.push(this.mapConsentService.isConsentGiven.subscribe(isConsentGiven => {
      this.isMapConsentGiven = isConsentGiven;
      if (!isConsentGiven) {
        this.destroyMap();
        return;
      }
      setTimeout(() => this.getMap());
    }));
 

    this.subscriptions.push(this.mapHandler.isResize.subscribe(() => {
      setTimeout(() => this.resizeMap(), 50);
    }));

    this.subscriptions.push(this.mapPositionService.mapPosition.subscribe(mapPosition => this.flyToMapPositon(mapPosition)));
    this.subscriptions.push(this.projectDataService.projects.subscribe(projects => {
      this.projects = projects;
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.projectGroups.subscribe(groups => {
      this.projectGroups = groups;
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.projectsRouteStations.subscribe(routeStations => {
      this.projectRouteStations = routeStations;
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.projectsRouteStationsWithParam.subscribe(routeStations => {
      this.projectRouteStationsWithParam = routeStations ?? [];
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.activeProject.subscribe(activeProject => {
      this.activeProject = activeProject;
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.activeGroup.subscribe(activeGroup => {
      this.activeGroup = activeGroup;
      this.renderCurrentLevel();
    }));
    this.subscriptions.push(this.projectDataService.activeRouteStation.subscribe(activeRouteStation => {
      this.activeRouteStation = activeRouteStation;
      this.renderCurrentLevel();
    }));
  }

  private async getMap(): Promise<void> {
    if (!this.isMapConsentGiven || this.map) return;

    await this.mapConsentService.loadGoogleMaps();
    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
    await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    this.map = new Map(document.getElementById("mapSettings") as HTMLElement, {
      center: { lat: 52, lng: 10 },
      zoom: 6,
      mapId: "avs-settings-map",
      mapTypeControl: true,
      scaleControl: true,
      maxZoom: 19,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        mapTypeIds: ["roadmap", "terrain", "satellite", "hybrid"],
      },
      gestureHandling: "greedy",
      clickableIcons: false,
      draggableCursor: "default",
      draggingCursor: "grabbing",
    });
    this.map.addListener("click", (event: google.maps.MapMouseEvent) => this.updatePositionDraftFromMapClick(event));
  }

  private destroyMap(): void {
    this.clearMarkers();
    this.map = undefined;
  }

  private flyToMapPositon(mapPosition: MapPosition): void {
    if (!this.map) return;
    this.map.panTo({ lat: mapPosition.latitude, lng: mapPosition.longitude });
    this.map.setZoom(mapPosition.zoom);
    this.cdr.detectChanges();
  }

  private updatePositionDraftFromMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    const latitude = event.latLng.lat();
    const longitude = event.latLng.lng();
    const target = this.activeRouteStation ? 'routeStation' : this.activeGroup ? 'group' : 'project';
    this.updateActiveElementPosition(latitude, longitude, target);
    this.projectDataService.updateMapPositionDraft(latitude, longitude, target);
    this.map?.panTo({ lat: latitude, lng: longitude });
    this.renderCurrentLevel();
  }

  private renderCurrentLevel(): void {
    if (!this.map) return;

    this.clearMarkers();

    if (this.activeRouteStation) {
      this.focusActiveRouteStation();
      const activeRouteStationMarker = this.createActiveRouteStationMarker();
      if (activeRouteStationMarker) {
        this.markers.push(activeRouteStationMarker);
      }
      return;
    }

    if (this.activeGroup) {
      this.focusActiveGroup();
      const routeStationsWithParam = this.projectRouteStationsWithParam
        .filter(routeStation => routeStation.routeStation.groupId === this.activeGroup?.id);
      if (routeStationsWithParam.length > 0) {
        this.markers.push(...routeStationsWithParam
          .filter(routeStation => this.hasValidCoordinates(this.getRouteStationPosition(routeStation)))
          .map(routeStation => this.createRouteStationWithParamMarker(routeStation)));
        if (this.hasValidCoordinates(this.getPosition(this.activeGroup))) {
          this.markers.push(this.createGroupMarker(this.activeGroup, 1000));
        }
        return;
      }
      this.markers.push(...this.projectRouteStations
        .filter(routeStation => routeStation.groupId === this.activeGroup?.id)
        .filter(routeStation => this.hasValidCoordinates(this.getPosition(routeStation)))
        .map(routeStation => this.createRouteStationMarker(routeStation)));
      if (this.hasValidCoordinates(this.getPosition(this.activeGroup))) {
        this.markers.push(this.createGroupMarker(this.activeGroup, 1000));
      }
      return;
    }

    if (this.activeProject) {
      this.focusActiveProject();
      this.markers.push(...this.projectGroups
        .filter(group => group.projectId === this.activeProject?.id)
        .filter(group => this.hasValidCoordinates(this.getPosition(group)))
        .map(group => this.createGroupMarker(group)));
      if (this.hasValidCoordinates(this.getPosition(this.activeProject))) {
        this.markers.push(this.createProjectMarker(this.activeProject, 1000));
      }
      return;
    }

    this.markers = this.projects
      .filter(project => project.isActive)
      .filter(project => this.hasValidCoordinates(this.getPosition(project)))
      .map(project => this.createProjectMarker(project));
  }

  private updateActiveElementPosition(latitude: number, longitude: number, target: 'project' | 'group' | 'routeStation'): void {
    switch (target) {
      case 'project':
        if (!this.activeProject) return;
        this.activeProject.latitude = latitude;
        this.activeProject.longitude = longitude;
        return;
      case 'group':
        if (!this.activeGroup) return;
        this.activeGroup.latitude = latitude;
        this.activeGroup.longitude = longitude;
        return;
      case 'routeStation':
        if (!this.activeRouteStation) return;
        this.activeRouteStation.latitude = latitude;
        this.activeRouteStation.longitude = longitude;
        return;
    }
  }

  private focusActiveProject(): void {
    if (!this.map || !this.activeProject) return;

    const position = this.getPosition(this.activeProject);
    if (!this.hasValidCoordinates(position)) return;

    this.map.panTo(position);
    if ((this.map.getZoom() ?? 0) < 12) {
      this.map.setZoom(12);
    }
  }

  private focusActiveGroup(): void {
    if (!this.map || !this.activeGroup) return;

    const position = this.getPosition(this.activeGroup);
    if (!this.hasValidCoordinates(position)) return;

    this.map.panTo(position);
    if ((this.map.getZoom() ?? 0) < 14) {
      this.map.setZoom(14);
    }
  }

  private focusActiveRouteStation(): void {
    if (!this.map || !this.activeRouteStation) return;

    const activeRouteStationWithParam = this.projectRouteStationsWithParam
      .find(routeStation => routeStation.routeStation.id === this.activeRouteStation?.id);
    const position = this.getBestRouteStationPosition(this.activeRouteStation, activeRouteStationWithParam);

    if (!this.hasValidCoordinates(position)) return;

    this.map.panTo(position);
    if ((this.map.getZoom() ?? 0) < 16) {
      this.map.setZoom(16);
    }
  }

  private createProjectMarker(project: Project, zIndex = 1): google.maps.marker.AdvancedMarkerElement {
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat: project.latitude, lng: project.longitude },
      content: this.createMarkerContent('project', project.name),
      title: project.name,
      zIndex,
    });
    return marker;
  }

  private createGroupMarker(group: ProjectGroup, zIndex = 1): google.maps.marker.AdvancedMarkerElement {
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: { lat: group.latitude, lng: group.longitude },
      content: this.createMarkerContent('group', group.name),
      title: group.name,
      zIndex,
    });
    return marker;
  }

  private createRouteStationMarker(routeStation: ProjectRouteStation, zIndex = 1): google.maps.marker.AdvancedMarkerElement {
    const position = this.getPosition(routeStation);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position,
      content: this.createMarkerContent('routeStation', routeStation.name),
      title: routeStation.name,
      zIndex,
    });
    return marker;
  }

  private createRouteStationWithParamMarker(routeStation: ProjectRouteStationWithParam, zIndex = 1): google.maps.marker.AdvancedMarkerElement {
    const position = this.getRouteStationPosition(routeStation);
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position,
      content: this.createMarkerContent('routeStation', routeStation.routeStation.name),
      title: routeStation.routeStation.name,
      zIndex,
    });
    return marker;
  }

  private createActiveRouteStationMarker(): google.maps.marker.AdvancedMarkerElement | undefined {
    if (!this.activeRouteStation) return undefined;

    const activeRouteStationWithParam = this.projectRouteStationsWithParam
      .find(routeStation => routeStation.routeStation.id === this.activeRouteStation?.id);
    const position = this.getBestRouteStationPosition(this.activeRouteStation, activeRouteStationWithParam);

    if (!this.hasValidCoordinates(position)) return undefined;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position,
      content: this.createMarkerContent('routeStation', this.activeRouteStation.name),
      title: this.activeRouteStation.name,
      zIndex: 1000,
    });
    return marker;
  }

  private createMarkerContent(type: 'project' | 'group' | 'routeStation', title: string): HTMLElement {
    const content = document.createElement("div");
    content.classList.add("property", "settings-map-marker");
    content.innerHTML = `
      <div class="icon">
        <img class="settings-map-marker-icon" src="${this.getIconPath(type)}" alt="">
        <div class="details">${title}</div>
      </div>
    `;
    return content;
  }

  private getIconPath(type: 'project' | 'group' | 'routeStation'): string {
    switch (type) {
      case 'project':
        return `${location.origin}/assets/icons/folder-tree-solid-full.svg`;
      case 'group':
        return `${location.origin}/assets/icons/folder-solid.svg`;
      case 'routeStation':
        return `${location.origin}/assets/icons/map-pin-solid.svg`;
    }
  }

  private getPosition(item: { latitude: number | null | undefined, longitude: number | null | undefined }): google.maps.LatLngLiteral {
    return { lat: item.latitude as number, lng: item.longitude as number };
  }

  private getRouteStationPosition(routeStation: ProjectRouteStationWithParam): google.maps.LatLngLiteral {
    return {
      lat: routeStation.schemaInfo?.gps?.data?.latitude
        ?? routeStation.routeStation.latitude,
      lng: routeStation.schemaInfo?.gps?.data?.longitude
        ?? routeStation.routeStation.longitude
    };
  }

  private getBestRouteStationPosition(routeStation: ProjectRouteStation, routeStationWithParam?: ProjectRouteStationWithParam): google.maps.LatLngLiteral {
    const configuredPosition = this.getPosition(routeStation);
    if (this.hasValidCoordinates(configuredPosition)) return configuredPosition;
    if (routeStationWithParam) return this.getRouteStationPosition(routeStationWithParam);
    return configuredPosition;
  }

  private hasValidCoordinates(position: google.maps.LatLngLiteral): boolean {
    return Number.isFinite(position.lat)
      && Number.isFinite(position.lng)
      && position.lat !== 0
      && position.lng !== 0;
  }

  private clearMarkers(): void {
    this.markers.forEach(marker => {
      marker.map = null;
      marker.content = null;
    });
    this.markers = [];
  }

  resizeMap(): void {
    if (!this.map) return;
    const center = this.map.getCenter();
    if (center) {
      this.map.setCenter(center);
    }
  }
}
